import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {

  ArrowDown,
  ArrowRightCircle,
  
} from 'lucide-react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import WhatsAppCommunityBanner from '../components/utilities/WhatsAppCommunityBanner';

const RelationshipConcernsPage: React.FC = () => {
  const [currentText, setCurrentText] = useState("Connection");
  const textCycle = ["Connection", "Understanding", "Growth", "Harmony"];

  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      const currentIndex = textCycle.indexOf(currentText);
      const nextIndex = (currentIndex + 1) % textCycle.length;
      setCurrentText(textCycle[nextIndex]);
    }, 2000);

    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [currentText]);

  const issuesCards = [
    {

      title: "Communication",
      description: "If you can talk well together, you&#39;re less likely to have other problems. Good, honest communication is key. People communicate differently, but relationships need regular, effective communication to do well. You might have stopped talking like you used to, or every conversation turns into a fight. Therapy can help you figure out where communication breaks down. It teaches you not to expect your partner to read your mind, to be open and honest about your feelings, listen well, and think before you respond.",

    },
    {

      title: "Arguments",
      description: "It's okay to disagree, but mean arguments don&#39;t help. Successful couples have rules for arguing, like breathing before responding, not using bad language, not calling names, and staying focused on the issue. Therapy can help you express feelings like anger or sadness without being mean.",

    },
    {

      title: "Not Feeling Close Anymore",
      description: "Relationships change over time, and you and your partner might change as individuals. You need to make an effort to spend time reconnecting. Therapy can help you talk about how you&#39;re both growing and how you see the future.",

    },
    {
      title: "Problems With Sex And Intimacy",
      description: "This is a common issue. Therapy, sometimes with a sex therapist, can help you talk openly about what you both want and need, respecting each other's desires."
    },
    {
      title: "Infidelity",
      description: "If one partner has cheated, it&#39;s very painful. Infidelity is defined as any physical, emotional, or sexual interaction that hurts the relationship&#39;s trust and closeness. Therapy can help couples decide if they can heal from it. It requires honesty about what happened and working on the underlying reasons. Healing takes time and commitment from both partners."
    },
    {
      title: "Money",
      description: "Arguments about money are common and can cause serious problems. Therapy can help couples understand who is responsible for what and create a simple budget to avoid arguments."
    },
    {
      title: "Trauma",
      description: "Stress from difficult life events like losing a loved one, financial problems, illness, or past abuse can affect a relationship. Therapy helps you support each other through these times."
    },
    {
      title: "Not Showing Gratitude",
      description: "Everyone wants to feel appreciated for their efforts. Therapy can encourage regularly expressing thanks."
    },
    {
      title: "Issues With Children",
      description: "Parenting can be hard, especially if you don&#39;t have a clear plan or present a united front. Therapy can help couples work together on parenting issues."
    },
    {
      title: "Relationship Feels Boring",
      description: "Keeping a relationship exciting takes effort. Therapy can help couples find ways to reconnect and spice things up."
    },
    {
      title: "Trust Issues",
      description: "Trust is very important. If you question if you can trust your partner, or they have trouble trusting you, therapy can help you talk about it and work to overcome these issues."
    },
    {
      title: "Safety Concerns",
      description: "Abuse (verbal, emotional, or physical) is never okay and needs to be addressed immediately. Therapy can help, and sources also point to resources like the National Domestic Violence Hotline."
    },
    {
      title: "Changes In Life Goals",
      description: "If you and your partner want different things in the future, it can cause problems. Therapy helps you talk about your goals and what is realistic."
    },
    {
      title: "Having The Same Fight Over And Over",
      description: "Repeating the same argument can be a problem. Therapy helps you get to the real reason behind the fight so you can try to change the patterns."
    }

  ];

  const typesCards = [
    {

      title: "Emotionally Focused Couples Therapy (EFCT)",
      description: "This type focuses on helping couples understand their emotional connections and patterns. It helps partners identify their needs and support each other. It&#39;s based on the idea that humans need to connect emotionally."
    },
    {

      title: "Gottman Method",
      description: "This method is based on research that looked at happy and unhappy relationships. It teaches couples how to use the patterns seen in happy relationships and avoid the patterns seen in unhappy ones. The therapist acts like a coach, teaching skills and pointing out unhelpful ways of interacting."
    },
    {

      title: "Integrative Behavioral Couples Therapy (IBCT)",
      description: "This type mixes ideas about behaviour and thinking. It focuses on changing behaviors and improving skills like communication and problem-solving."
    },
    {
      title: "Narrative Therapy",
      description: "This approach helps couples look at their problems as something outside of them, rather than seeing each other as the problem. It also helps couples rewrite their shared story to be more positive."
    },
    {
      title: "Solution Focused Therapy",
      description: "This type focuses on the couple&#39;s goals and solutions instead of just talking about the problems. It helps the couple find times when things worked well and figure out how to make that happen more often. However, sources suggest this might be better for shorter-term issues and less sustainable for long-term relationship problems."
    }
  ];

  const benefitsTypes = [
    {
      title: "Understand Your Relationship Better",
      description: "Therapy helps you see how you and your partner interact. Like, who usually makes decisions? Is it fair for both of you? Do you keep falling into the same negative ways of talking? Are there certain things you always argue about? How do you handle arguments? \n Looking at these questions helps you see patterns in your relationship, which is important for healing it and making your connection stronger."
    },
    {
      title: "A Neutral Person To Help",
      description: "A good couples therapy space is where you can speak freely. The therapist can give unbiased feedback. Sometimes, hearing what someone else hears can help you see your relationship differently. \n This neutral person can give you constructive feedback about what they are hearing, which can make a big difference. A therapist can help you think about decisions and how your actions might help you reach your goal of keeping the relationship strong."
    },
    {
      title: "Safe Space For Both",
      description: "An argument with your partner may make you less inclined to be open and honest. What you need is a safe space with rules and boundaries that are kept by the neutral person i.e. your therapist. Talking effectively and freely becomes the key at that point."
    },
    {
      title: "See Your Partner’s Side",
      description: "It's normal to see things only from your own point of view. Our feelings are a priority for us, and it can be hard to understand the other’s perspective. Therapy can help you look objectively at both sides of a disagreement. This helps you avoid misunderstanding and start understanding the real reason for a problem."
    },
    {
      title: "Solve relationship problems",
      description: "It's common for couples to get stuck on certain topics, like whether to have kids, moving house, or even just arguing about chores like laundry or dishes. Therapy helps you look at the real issue causing these conflicts, deal with them, and figure out a solution."
    },
    {
      title: "Ways to Handle Tough Times",
      description: "No relationship is perfect, and you&#39;ll face difficult times. Learning good coping skills helps you and your partner get through these challenges. Learning ways to deal with anger, sadness, or stress in therapy prepares you for future difficulties."
    },
    {
      title: "Understand Feelings About Relationship",
      description: "Figure out what you really want in a relationship. You might start hoping to fix things and stay together, or you might wonder if the relationship can even be saved. \n Therapy helps you explore, understand, and make your feelings clearer. Free conversation can help you figure out your goals and ways to reach them. Making your feelings clear is a big benefit."
    },
    {
      title: "Rebuild Trust",
      description: "Many couples seek therapy because trust has been broken, perhaps by cheating, lying, or money problems. Therapy is a place where you can say that trust is missing, explore why it was broken, work on forgiveness, and set new boundaries so healing can start. Rebuilding trust is hard but possible with the right help."
    },
    {
      title: "Feel Connected",
      description: "Finding intimacy and connection is crucial in a relationship. Intimacy problems are common, especially in long-term relationships where the spark feels like it's gone. Get back that connection by opting for couples therapy. \n Focusing on each other&#39;s needs and desires is important, but therapy also works on communication and respect, which helps intimacy grow naturally."
    },
    {
      title: "Better Communication Skills",
      description: "Clichés exist for a reason. Communication is key is one of the most widely used ones, but it really works. Your therapist may help you practice it in a supervised safe space. The improvement may help not only your relationship but other parts of your life too."
    },
    {
      title: "Understand Yourself Better",
      description: "People don’t expect personal growth and knowing yourself better, when going to therapy. You learn a lot about yourself and your relationship along the way. This helps in understanding each other’s needs and motivations deeply to forge a stronger partnership."
    }
  ]


  const testimonials = [
    {
      quote: "Our relationship was on the brink of falling apart. Through compassionate guidance, we learned to truly listen and understand each other. Now, we're stronger than ever.",

      image: "/images/avatar1.png"
    },
    {
      quote: "Communication was our biggest challenge. This counseling helped us develop tools to express our needs, resolve conflicts, and rebuild our emotional connection.",

      image: "/images/avatar3.png"
    },
    {
      quote: "After years of misunderstandings, we finally found a safe space to heal. The therapists helped us transform our relationship from surviving to thriving.",

      image: "/images/avatar5.png"
    }
  ];


  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>Psychologist for Couples Therapy &amp; Relationship Counselling</title>
        <meta name="description" content="Struggling with relationship problems? Talk to psychologists with expertise in marriage issues,
relationship problems, couples therapy, intimacy issues. Call Now." />

        {/* Open Graph / Social Media Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Psychologist for Couples Therapy &amp; Relationship Counselling" />
        <meta
          property="og:description"
          content="Struggling with relationship problems? Talk to psychologists with expertise in marriage issues,
relationship problems, couples therapy, intimacy issues. Call Now."
        />
        <meta property="og:url" content="https://grehpraveshtherapy.com/relationship-concerns" />
        <meta property="og:image" content="/images/relationship-concern.png" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Relationship Counseling - Path to Emotional Wellness" />
        <meta
          name="twitter:description"
          content="Compassionate relationship support. Learn communication skills, rebuild trust, and create lasting emotional connections."
        />
        <meta name="twitter:image" content="/images/relationship-concern.png" />

        {/* Keywords */}
        <meta
          name="keywords"
          content="relationship counseling, couples therapy, communication skills, emotional healing, relationship wellness, trust building, couples support"
        />

        {/* Structured JSON-LD Schema Markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            "name": "Relationship Counseling Services",
            "description": "Comprehensive relationship support helping couples improve communication, rebuild trust, and create deeper emotional connections.",
            "offers": {
              "@type": "Offer",
              "description": "Professional couples therapy and relationship counseling"
            },
            "serviceType": [
              "Couples Counseling",
              "Communication Therapy",
              "Relationship Healing"
            ],
            "areaServed": {
              "@type": "GeoShape",
              "additionalType": "Service Area"
            },
            "hasCredential": {
              "@type": "EducationalOccupationalCredential",
              "name": "Licensed Relationship Therapists"
            },
            "additionalType": [
              "Emotional Support",
              "Relationship Guidance",
              "Interpersonal Therapy"
            ]
          })}
        </script>
      </Helmet>

      <main className="bg-white">
        {/* Landing Section */}
        <section className="curvy-bg-container relative min-h-screen flex flex-col md:flex-row overflow-hidden bg-gradient-to-br from-blue-600 to-blue-400">
          {/* Background layers */}
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-indigo-800 to-indigo-300 rounded-br-[100%] z-0 opacity-50"></div>
          <div className="absolute top-0 right-0 w-full h-full bg-contain bg-no-repeat bg-right-top opacity-10 md:opacity-20 z-0"
            style={{ backgroundImage: "url('/images/relationship-concern.png')" }}
          ></div>

          <div className="w-full md:w-7/12 z-10 flex items-center text-center mt-28 md:mt-0 text-center px-6 md:pl-24 md:pr-0 relative">
            <div className="max-w-2xl w-full space-y-6 md:mt-20">




              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-4xl md:text-4xl text-white font-semibold md:font-bold mt-20 md:mt-0"
              >
                Therapy for Relationship Issues
              </motion.h1>



              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="hidden md:block text-white/90 md:text-xl max-w-xl"
              >
                A relationship is like two people building something together. Sometimes, you might argue about
                how to build it, or maybe the tools aren&#39;t working right, or you just feel like you&#39;re not on the same
                page anymore. That’s where one may look to consult a psychologist for relationship problems.
              </motion.p>

              <motion.div
                className="pt-8 space-y-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7 }}
              >
                <button
                  className="bg-white text-blue-700 flex items-center justify-center text-center px-6 py-3 text-sm md:text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all w-full md:w-auto mx-auto"
                  onClick={() => navigate('/book-appointment')}
                >
                  Book Appointment Now
                  <ArrowRightCircle className="ml-3 w-7 h-7 text-blue-700" />
                </button>

                <motion.div
                  className="block md:hidden w-full h-96 md:h-full"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9, delay: 3 }}
                >
                  <img
                    src="/images/relationship-concern.png"
                    alt="relationship support"
                    className="w-full h-full object-cover rounded-xl opacity-80"
                  />
                </motion.div>
              </motion.div>
            </div>
          </div>

          <motion.div
            className="hidden md:block absolute top-0 right-0 w-5/12 h-full z-0"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 3 }}
          >
            <img
              src="/images/relationship-concern.png"
              alt="relationship support"
              className="absolute inset-0 w-full h-full object-cover opacity-70"
            />
          </motion.div>
        </section>

        <div className="text-center py-4 bg-white">
          <ArrowDown
            className="animate-bounce mx-auto text-blue-600"
            size={32}
          />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16 space-y-12 sm:space-y-16 lg:space-y-20">

          {/* What's Relationship Counselling Section */}
          <section className="max-w-7xl mx-auto">
            <div className="text-center mb-8 sm:mb-12 lg:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight px-4">
                What's Relationship Counselling?
              </h2>
            </div>
            <div className="max-w-5xl mx-auto">
              <div className="bg-gradient-to-br from-blue-50 via-purple-50 to-yellow-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 shadow-sm border border-violet-100">
                <div className="space-y-4 sm:space-y-6 lg:space-y-8">
                  <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed font-medium">
                    Relationship therapy, also known as couples therapy, marriage counseling, or marital therapy, is like
                    getting help from a friendly expert who knows a lot about building things together.
                  </p>
                  <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed">
                    However, it's not necessary for both partners to be involved. A lot of the instances involve only one
                    of the partner with their own particular concerns regarding the relationship. It could be about their
                    own habits and specific issues, or something about the partner that needs to be resolved.
                  </p>
                  <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed">
                    It's a special kind of therapy, often a type of family therapy. Its main job is to help couples explore
                    the reasons why they have conflicts. It also focuses on making their communication skills better so
                    the relationship can heal and get stronger.
                  </p>
                  <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed">
                    When both people in the relationship are really trying, couples therapy can help build a partnership
                    based on mutual respect, caring, and trust. Think of it as bringing in a neutral person to help you and
                    your partner talk about things in a new way.
                  </p>
                  <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed font-medium">
                    Couples therapy, both in person or online, can be really good for any romantic relationship. The
                    more effort you are likely to put, the better the results are expected to be.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Benefits of Couples Therapy Section */}
          <section className="max-w-7xl mx-auto">
            <div className="text-center mb-8 sm:mb-12 lg:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 px-4">
                Benefits of Couples Therapy
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10 mb-8 sm:mb-12">
              {benefitsTypes.map((card, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="bg-white rounded-2xl sm:rounded-3xl shadow-lg border border-gray-100 overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:border-violet-200 p-6 sm:p-8"
                >
                  <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 text-center mb-4 sm:mb-6">
                    {card.title}
                  </h3>
                  <p className="text-center text-gray-600 text-base sm:text-lg leading-relaxed">
                    {card.description}
                  </p>
                </motion.div>
              ))}
            </div>

            <div className="max-w-5xl mx-auto">
              <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 shadow-sm border border-blue-100">
                <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed">
                  You might find you can set clearer boundaries not just in your relationship, but also at work or
                  with friends. Your skills for solving disagreements might get better, so you have less drama. You
                  might learn not to react so quickly, and this can affect other parts of your life too. Couples
                  counselling can end up changing many areas of your life.
                </p>
              </div>
            </div>
          </section>


          {/* How Effective Is Couples Therapy Section */}
          <section className="max-w-7xl mx-auto">
            <div className="text-center mb-8 sm:mb-12 lg:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight px-4">
                How Effective Is Couples Therapy?
              </h2>
            </div>
            <div className="space-y-6 sm:space-y-8 lg:space-y-10 max-w-5xl mx-auto">
              <div className="bg-gradient-to-br from-blue-50 via-cyan-50 to-indigo-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 shadow-sm border border-blue-100">
                <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed">
                  A tool used to measure how well therapy works over time, called the Dyadic Adjustment Scale (DAS),
                  shows good results for couples who get counseling, sometimes for up to 2 years after they finish
                  therapy.
                </p>
              </div>

              <div className="bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 shadow-sm border border-emerald-100">
                <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed">
                  Couples often opt for therapy when things are the worst, to the point that the relationship might
                  end if they don't get help. For couples who are willing and ready to make changes, therapy can feel
                  effective right away. For those who are less sure, it might take longer, but gradual effectiveness can
                  be inevitable.
                </p>
              </div>
            </div>
          </section>

          {/* Types of Relationship Therapy Section */}
          <section className="max-w-7xl mx-auto">
            <div className="text-center mb-8 sm:mb-12 lg:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 px-4">
                Types of Relationship Therapy
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10 mb-8 sm:mb-12">
              {typesCards.map((card, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="bg-white rounded-2xl sm:rounded-3xl shadow-lg border border-gray-100 overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:border-blue-200 p-6 sm:p-8"
                >
                  <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 text-center mb-4 sm:mb-6">
                    {card.title}
                  </h3>
                  <p className="text-center text-gray-600 text-base sm:text-lg leading-relaxed">
                    {card.description}
                  </p>
                </motion.div>
              ))}
            </div>

            <div className="max-w-5xl mx-auto">
              <div className="bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 shadow-sm border border-rose-100">
                <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed">
                  Relationship therapy can be incredibly important if you want to strengthen or fix your relationship. It
                  gives you tools to communicate well and build a strong, positive partnership.
                </p>
              </div>
            </div>
          </section>

          {/* Common Couples' Issues Section */}
          <section className="max-w-7xl mx-auto">
            <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 xl:p-16 shadow-lg border border-blue-100">
              <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 px-4">
                  Common Couples' Issues
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10">
                {issuesCards.map((card, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    className="bg-white rounded-2xl sm:rounded-3xl shadow-lg border border-gray-100 overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:border-indigo-200 p-6 sm:p-8"
                  >
                    <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 text-center mb-4 sm:mb-6">
                      {card.title}
                    </h3>
                    <p className="text-center text-gray-600 text-base sm:text-lg leading-relaxed">
                      {card.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Additional Information Section */}
          <section className="max-w-7xl mx-auto">
            <div className="space-y-6 sm:space-y-8 lg:space-y-10 max-w-5xl mx-auto">
              <div className="bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 shadow-sm border border-amber-100">
                <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed">
                  Fixing relationship problems starts with wanting to make it better and both partners being willing to
                  work on it. It can even be enjoyable if you're both dedicated. Therapy can help you take
                  responsibility for your part. Even simple things like saying "please" and "thank you," arguing without
                  being mean, and asking about each other's needs can help. Therapy helps you figure out where the
                  problem is coming from and how to improve the situation.
                </p>
              </div>

              <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 shadow-sm border border-green-100">
                <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed font-medium">
                  Seeking help for relationship challenges is a sign of strength, not weakness. Couples therapy is a
                  valuable way to work through problems and make your relationship stronger. If you're willing to
                  take the first step, click on "Schedule Consultation" below.
                </p>
              </div>
            </div>
          </section>

          {/* Testimonial Section */}
          <section className="bg-blue-100 bg-opacity-50 rounded-xl p-6 md:p-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-center text-gray-800 mb-8 md:mb-12">
              Relationship Transformation Stories
            </h2>
            <div className="grid sm:grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="bg-white rounded-xl p-6 md:p-8 shadow-lg relative flex flex-col"
                >

                  <img
                    src={testimonial.image}
                    alt='testimonial'
                    className="w-20 h-20 rounded-full mx-auto mb-4 border-4 border-white shadow-md"
                  />

                  <div className="text-center flex-grow">
                    <p className="text-gray-600 italic mb-4 relative">
                      "{testimonial.quote}"
                    </p>
                    <h3 className="font-semibold text-gray-800">

                    </h3>

                  </div>
                </motion.div>
              ))}
            </div>
          </section>
          <WhatsAppCommunityBanner />

          {/* Call to Action */}
          <section className="bg-blue-600 bg-opacity-10 rounded-xl p-16 text-center">
            <h2 className="text-4xl font-semibold text-gray-800 mb-6">
              Your Relationship, Your Journey
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-12">
              Every relationship has its unique story. We're here to support you
              in writing a chapter of understanding, growth, and deeper connection.
            </p>
            <div className="flex flex-col md:flex-row justify-center space-x-4 md:space-x-4 space-y-4 md:space-y-0 w-full max-w-md mx-auto">
              <Link
                className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-opacity-90 transition-colors font-raleway-regular"
                to="/book-appointment"
              >
                Schedule Consultation
              </Link>

            </div>
          </section>
        </div>
      </main>
    </>
  );
};

export default RelationshipConcernsPage;